// TES-B2C1B1M2 — Clock Out finish-time bounds in device-local time.
// Backend window (minute-based, frozen): pressMinute − 5 ≤ reported ≤
// pressMinute + 10, and reported ≥ paired CLOCK_IN instant.
const MIN = 60000;

export const fmtHM = (d) => `${String(d.getHours()).padStart(2, "0")}:${
    String(d.getMinutes()).padStart(2, "0")}`;

export const localYMD = (d) => `${d.getFullYear()}-${
    String(d.getMonth() + 1).padStart(2, "0")}-${
    String(d.getDate()).padStart(2, "0")}`;

// Absolute-ms arithmetic only: local setters re-resolve ambiguous wall
// times at DST fall-back. Local minutes are used for grid alignment.
const floorMinute = (d) => new Date(
    d.getTime() - d.getSeconds() * 1000 - d.getMilliseconds());

// Canonical 10-minute rounding: remainder 0–5 down, 6–9 up.
export function canonicalRound10(now) {
    const p = floorMinute(new Date(now));
    const r = p.getMinutes() % 10;
    return new Date(p.getTime() - r * MIN + (r <= 5 ? 0 : 10 * MIN));
}

function ceil10(x) {
    const d = new Date(x);
    const p = (d.getSeconds() || d.getMilliseconds())
        ? new Date(floorMinute(d).getTime() + MIN) : d;
    const r = p.getMinutes() % 10;
    return r ? new Date(p.getTime() + (10 - r) * MIN) : p;
}

// Epoch-minute bounds; stepping by 10 stays on the local 10-min grid.
export function clockOutBounds(clockInIso, now) {
    const press = floorMinute(new Date(now));
    const low = new Date(press.getTime() - 5 * MIN);
    const inD = new Date(clockInIso);
    const start = ceil10(inD > low ? inD : low);
    const startMin = Math.round(start.getTime() / MIN);
    let endMin = Math.round(canonicalRound10(now).getTime() / MIN);
    // Canonical value precedes the paired Clock In → nearest valid slot.
    if (endMin < startMin && start.getTime() <= press.getTime() + 10 * MIN) {
        endMin = startMin;
    }
    return { startMin, endMin };
}

export const pickValidFinish = (options, preferred) => (
    options.includes(preferred) ? preferred : options[options.length - 1] || "");

// TES-B2C1B1M3 — local wall time → instant; a repeated DST hour
// resolves to the occurrence nearest `near`.
export function localInstant(ymd, hm, near) {
    const [y, mo, d] = ymd.split("-").map(Number);
    const [hh, mm] = hm.split(":").map(Number);
    const base = new Date(y, mo - 1, d, hh, mm);
    const same = (x) => x.getFullYear() === y && x.getMonth() === mo - 1
        && x.getDate() === d && x.getHours() === hh && x.getMinutes() === mm;
    const cands = [base, new Date(base.getTime() - 3600000),
        new Date(base.getTime() + 3600000)].filter(same);
    if (!cands.length) return base;
    return cands.reduce((a, b) => (
        Math.abs(b - near) < Math.abs(a - near) ? b : a));
}

// "Yes, ended at <end>" only when the end is reached (≤ canonical
// press) and not before the paired Clock In. Mirrors the backend.
export function scheduledEndConfirmable(shift, clockInIso, now) {
    if (!shift?.end_time || !(shift.end_date || shift.work_date)) return false;
    const end = localInstant(
        shift.end_date || shift.work_date, shift.end_time, new Date(now));
    return end >= new Date(clockInIso) && end <= canonicalRound10(now);
}

// Clock In "No — different time": 10-minute slots from max(now − 16 h,
// previous session end) up to the canonical-rounded press (never future).
// Each option carries an explicit local date so overnight starts are unambiguous.
export function declaredStartOptions(now, floorIso) {
    const nowD = new Date(now);
    const today = localYMD(nowD);
    const lowBound = new Date(nowD.getTime() - 16 * 3600 * 1000);
    const floor = floorIso ? new Date(floorIso) : null;
    const startMin = Math.round(ceil10(floor && floor > lowBound ? floor : lowBound).getTime() / MIN);
    const endMin = Math.round(canonicalRound10(nowD).getTime() / MIN);
    const out = [];
    for (let m = startMin; m <= endMin; m += 10) {
        const d = new Date(m * MIN);
        const ymd = localYMD(d);
        out.push({ value: `${ymd} ${fmtHM(d)}`, date: ymd, time: fmtHM(d),
            label: `${ymd === today ? "Today" : "Yesterday"} ${fmtHM(d)}` });
    }
    return out;
}

// "Did you start at HH:MM?" only when the scheduled start is not in the
// future beyond the canonical +10 min band (backend mirrors this).
export function scheduledStartConfirmable(res, now) {
    if (res?.kind !== "PUBLISHED_SHIFT" || !res.start_time || !res.work_date) return false;
    const start = localInstant(res.work_date, res.start_time, new Date(now));
    return start.getTime() <= new Date(now).getTime() + 10 * MIN;
}
